
import {
  useRef,
  useState,
  useEffect,
  type ReactNode,
  type MouseEvent,
} from 'react';

interface TouchSliderProps {
  children: ReactNode[];
  gap?: number;
  showDots?: boolean;
  showArrows?: boolean;
  className?: string;
  itemClassName?: string;
  autoWidth?: boolean;
}

/**
 * TouchSlider – a lightweight carousel that works with mouse, touch and
 * keyboard navigation. It automatically updates the scroll state, disables
 * arrows when scrolling is not possible and provides a robust fallback
 * when the DOM element is unavailable.
 */
export default function TouchSlider({
  children,
  gap = 16,
  showDots = true,
  showArrows = false,
  className = '',
  itemClassName = '',
  autoWidth = false,
}: TouchSliderProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const totalItems = children.length;

  /** Updates the state that drives arrows and dots */
  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;

    setCanScrollLeft(scrollLeft > 5);
    setCanScrollRight(scrollLeft < maxScroll - 5);

    // Guard against division by zero
    if (totalItems === 0) {
      setActiveIndex(0);
      return;
    }

    const itemWidth = scrollWidth / totalItems;
    const idx = Math.round(scrollLeft / itemWidth);
    setActiveIndex(Math.min(idx, totalItems - 1));
  };

  /** Attach scroll listener once the component mounts */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    el.addEventListener('scroll', updateScrollState, { passive: true });
    // Initialise state on mount
    updateScrollState();

    return () => {
      el.removeEventListener('scroll', updateScrollState);
    };
  }, [totalItems]);

  /** Scrolls one “page” left or right */
  const scrollTo = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;

    // Use a fraction of the visible width to create a smooth carousel feel
    const step = el.clientWidth * 0.85;
    el.scrollBy({
      left: direction === 'left' ? -step : step,
      behavior: 'smooth',
    });
  };

  /** Scrolls directly to the slide at the supplied index */
  const scrollToIndex = (index: number) => {
    const el = scrollRef.current;
    if (!el) return;

    if (totalItems === 0) return;

    const itemWidth = el.scrollWidth / totalItems;
    el.scrollTo({ left: itemWidth * index, behavior: 'smooth' });
  };

  /** Optional defensive click‑handler to avoid accidental
   *  navigation when the target element is missing.
   */
  const safeClick = (handler: () => void) => (e: MouseEvent) => {
    e.preventDefault();
    try {
      handler();
    } catch (err) {
      console.error('TouchSlider action failed:', err);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {showArrows && canScrollLeft && (
        <button
          onClick={safeClick(() => scrollTo('left'))}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-[#050505]/90 border border-[#00E5FF]/30 text-[#00E5FF] backdrop-blur-sm cursor-pointer hover:bg-[#00E5FF] hover:text-[#050505] transition-all duration-300 -ml-2 rounded-full"
          aria-label="Przewiń w lewo"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
      )}

      <div
        ref={scrollRef}
        className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory touch-pan-x"
        style={{
          gap: `${gap}px`,
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {children.map((child, index) => (
          <div
            key={index}
            className={`snap-start flex-shrink-0 ${itemClassName}`}
            style={autoWidth ? {} : { width: 'auto' }}
          >
            {child}
          </div>
        ))}
      </div>

      {showArrows && canScrollRight && (
        <button
          onClick={safeClick(() => scrollTo('right'))}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 flex items-center justify-center bg-[#050505]/90 border border-[#00E5FF]/30 text-[#00E5FF] backdrop-blur-sm cursor-pointer hover:bg-[#00E5FF] hover:text-[#050505] transition-all duration-300 -mr-2 rounded-full"
          aria-label="Przewiń w prawo"
        >
          <i className="ri-arrow-right-s-line text-xl" />
        </button>
      )}

      {showDots && totalItems > 1 && (
        <div className="flex items-center justify-center gap-2 mt-5">
          {Array.from({ length: totalItems }).map((_, i) => (
            <button
              key={i}
              onClick={safeClick(() => scrollToIndex(i))}
              className={`transition-all duration-300 cursor-pointer rounded-full ${
                i === activeIndex
                  ? 'w-8 h-2 bg-[#00E5FF]'
                  : 'w-2 h-2 bg-[#333333] hover:bg-[#555555]'
              }`}
              aria-label={`Przejdź do ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
